import { ExportButton } from "@/components/ExportButton";
import {
  DatagridConfigurable,
  List,
  SelectColumnsButton,
  SimpleList,
  TextField,
  TopToolbar,
  SelectInput,
  FunctionField,
  NumberField,
  CreateButton,
  type RaRecord,
} from "react-admin";
import { Chip, useMediaQuery, useTheme } from "@mui/material";
import { formatDisplayDateTime } from "@/utils/date-format";
import {
  BROADCAST_KIND_CHOICES,
  SABIL_TYPE_FILTER_CHOICES,
  describeBroadcastListSecondaryText,
  formatBroadcastKindLabel,
} from "./components/broadcast-kind-label";
import { formatSabilOutstandingTypeLabel } from "./components/sabil-outstanding-type-label";

const ListActions = () => (
  <TopToolbar>
    <CreateButton />
    <SelectColumnsButton />
    <ExportButton />
  </TopToolbar>
);

const broadcastFilters = [
  <SelectInput
    source="status"
    choices={[
      { id: "PENDING", name: "Pending" },
      { id: "PROCESSING", name: "Processing" },
      { id: "COMPLETED", name: "Completed" },
      { id: "FAILED", name: "Failed" },
      { id: "CANCELLED", name: "Cancelled" },
    ]}
    alwaysOn={false}
    key="status"
  />,
  <SelectInput
    source="kind"
    label="Broadcast type"
    choices={BROADCAST_KIND_CHOICES}
    alwaysOn={false}
    key="kind"
  />,
  <SelectInput
    source="sabilType"
    label="Sabil/Mutawatteen type"
    choices={SABIL_TYPE_FILTER_CHOICES}
    alwaysOn={false}
    key="sabilType"
  />,
];

const statusColors: Record<string, "default" | "warning" | "success" | "error"> = {
  PENDING: "default",
  PROCESSING: "warning",
  COMPLETED: "success",
  FAILED: "error",
  CANCELLED: "default",
};

const StatusField = ({ record }: { record?: RaRecord }) => {
  if (!record) return null;
  const status = String(record.status ?? "");
  return <Chip label={status} color={statusColors[status] || "default"} size="small" />;
};

export default function WhatsappBroadcastList() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <List
      actions={<ListActions />}
      filters={broadcastFilters}
      sort={{ field: "createdAt", order: "DESC" }}
    >
      {isMobile ? (
        <SimpleList
          linkType="show"
          primaryText={(record) => String(record.name ?? "")}
          secondaryText={(record) => describeBroadcastListSecondaryText(record)}
          tertiaryText={(record) => <StatusField record={record} />}
        />
      ) : (
        <DatagridConfigurable rowClick="show" bulkActionButtons={false}>
          <TextField source="name" label="Broadcast Name" />
          <FunctionField
            label="Broadcast Type"
            render={(record: RaRecord) => (
              <Chip label={formatBroadcastKindLabel(record.kind as string)} size="small" />
            )}
          />
          <FunctionField
            label="Sabil/Mutawatteen Type"
            render={(record: RaRecord) => {
              const sabilType = (record.filterCriteria as { sabilType?: string } | null)?.sabilType;
              return sabilType ? formatSabilOutstandingTypeLabel(sabilType) : "—";
            }}
          />
          <TextField source="templateName" label="Template" />
          <FunctionField label="Status" render={(record) => <StatusField record={record} />} />
          <NumberField source="totalRecipients" label="Total" />
          <NumberField source="successCount" label="Success" />
          <NumberField source="failureCount" label="Failed" />
          <FunctionField
            label="Created"
            sortBy="createdAt"
            render={(record: RaRecord) => formatDisplayDateTime(record?.createdAt, { empty: "—" })}
          />
          <FunctionField
            label="Started"
            sortBy="startedAt"
            render={(record: RaRecord) => formatDisplayDateTime(record?.startedAt, { empty: "—" })}
          />
          <FunctionField
            label="Completed"
            sortBy="completedAt"
            render={(record: RaRecord) =>
              formatDisplayDateTime(record?.completedAt, { empty: "—" })
            }
          />
        </DatagridConfigurable>
      )}
    </List>
  );
}
